import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Search } from "lucide-react";
import { format, format as formatDate, parse } from "date-fns";
import { useTenant } from "@/contexts/TenantContext";
import { useDateLocale } from "@/lib/dateLocale";
import { publicApi } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";
import { SEO, SEOPresets } from "@/components/shared/SEO";
import { TripCard, type Trip } from "@/components/trips/TripCard";
import { TripCardSkeletonList } from "@/components/trips/TripCardSkeleton";
import { DatePriceCarousel } from "@/components/search/DatePriceCarousel";
import { ResultsHeader } from "@/components/results/ResultsHeader";
import { type SortOption } from "@/components/results/SortToggle";
import { EnhancedHeader } from "@/components/layout/EnhancedHeader";

interface City {
  id: number;
  name: string;
  state: string | null;
}

export function TripSearchResultsPage() {
  const { t } = useTranslation();
  const dl = useDateLocale();
  const { slug, tenant } = useTenant();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);

  const [sortBy, setSortBy] = useState<SortOption>("departure");

  const date = params.get("date") || "";
  const fromId = params.get("from") || "";
  const toId = params.get("to") || "";
  const passengers = parseInt(params.get("passengers") || "1", 10);
  const tripType = (params.get("tripType") || "one-way") as
    | "one-way"
    | "round-trip";

  const { data: cities = [] } = useQuery({
    queryKey: ["cities", slug],
    queryFn: async () => {
      if (!slug) return [];
      return publicApi.get<City[]>(`/api/public/${slug}/cities`);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: trips = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["trips", slug, date, fromId, toId, passengers],
    queryFn: async () => {
      if (!slug || !date || !fromId || !toId) return [];
      const queryParams = new URLSearchParams({
        originCityId: fromId,
        destinationCityId: toId,
        date,
        passengers: passengers.toString(),
      });
      return publicApi.get<Trip[]>(
        `/api/public/${slug}/trips/search?${queryParams.toString()}`,
      );
    },
    enabled: !!slug && !!date && !!fromId && !!toId,
    staleTime: 30 * 1000,
  });

  const originCity = cities.find((c) => c.id.toString() === fromId);
  const destinationCity = cities.find((c) => c.id.toString() === toId);

  // `date` is a plain yyyy-MM-dd label. Parsing it into a browser-local midnight
  // and rendering THAT in another zone shifted the heading by a day for any
  // browser east of the tenant — a UTC browser searching 1 July saw "Jun 30".
  let displayDate = date;
  try {
    const [y, m, d] = date.split("-").map(Number);
    if (y && m && d) {
      displayDate = formatDate(new Date(y, m - 1, d), dl.pattern("weekdayDateYear"), { locale: dl.locale });
    }
  } catch {
    // keep raw
  }

  useEffect(() => {
    if (slug) trackPageView("trip_results", slug);
  }, [slug]);

  const handleBackToSearch = () => setLocation(`/${slug}`);

  const handleDateSelect = (newDate: Date) => {
    const newDateStr = format(newDate, "yyyy-MM-dd");
    const newParams = new URLSearchParams({
      from: fromId,
      to: toId,
      date: newDateStr,
      passengers: passengers.toString(),
      tripType,
    });
    setLocation(`/${slug}/trips?${newParams.toString()}`);
  };

  const selectedDate = date
    ? parse(date, "yyyy-MM-dd", new Date())
    : new Date();

  const sortedTrips = useMemo(() => {
    if (!trips || trips.length === 0) return trips;
    return [...trips].sort((a, b) => {
      switch (sortBy) {
        case "departure":
          return (
            new Date(a.departureTime).getTime() -
            new Date(b.departureTime).getTime()
          );
        case "price":
          return parseFloat(a.segmentPrice) - parseFloat(b.segmentPrice);
        case "seats":
          return b.availableSeats - a.availableSeats;
        default:
          return 0;
      }
    });
  }, [trips, sortBy]);

  return (
    <div className="min-h-screen bg-stone-50 text-ink flex flex-col">
      <SEO {...SEOPresets.tripResults} />

      <EnhancedHeader />

      {/* Route context strip */}
      <section className="bg-white border-b hairline-strong">
        <div className="mx-auto max-w-5xl px-6 py-7 md:py-10">
          <button
            onClick={handleBackToSearch}
            className="text-eyebrow uppercase text-ink-muted hover:text-ink inline-flex items-center gap-1.5 mb-4 transition-colors focus-visible-ring"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("trips.backToSearch")}
          </button>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap text-ink">
                <span className="font-display text-[1.75rem] md:text-[2.25rem] leading-tight truncate">
                  {originCity?.name || t("common.loading")}
                </span>
                <ArrowRight
                  className="h-5 w-5 md:h-6 md:w-6 text-brand flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="font-display text-[1.75rem] md:text-[2.25rem] leading-tight truncate">
                  {destinationCity?.name || t("common.loading")}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-3 text-sm text-ink-muted tabular flex-wrap">
                <span>{displayDate}</span>
                <span className="h-1 w-1 bg-ink-subtle" aria-hidden="true" />
                <span>
                  {passengers}{" "}
                  {passengers === 1
                    ? t("search.passenger")
                    : t("search.passengers_plural")}
                </span>
                {tripType === "round-trip" && (
                  <>
                    <span
                      className="h-1 w-1 bg-ink-subtle"
                      aria-hidden="true"
                    />
                    <span className="uppercase tracking-wider text-eyebrow text-ink-subtle">
                      {t("search.roundTrip")}
                    </span>
                  </>
                )}
              </div>
            </div>

            <button
              onClick={handleBackToSearch}
              className="inline-flex items-center gap-2 border hairline-strong px-4 py-2.5 text-sm text-ink hover:border-ink transition-colors focus-visible-ring self-start md:self-auto"
            >
              <Search className="h-4 w-4" />
              {t("trips.modifySearch")}
            </button>
          </div>
        </div>
      </section>

      {/* Main content */}
      <main id="main-content" className="flex-1">
        <div className="mx-auto max-w-5xl px-6 py-8 md:py-10">
          {/* Date price carousel */}
          {fromId && toId && (
            <div className="mb-8">
              <div className="text-eyebrow uppercase text-ink-subtle mb-3">
                {t("trips.browsePrices", "Adjacent dates")}
              </div>
              <DatePriceCarousel
                originCityId={parseInt(fromId, 10)}
                destinationCityId={parseInt(toId, 10)}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
              />
            </div>
          )}

          {isLoading && <TripCardSkeletonList count={3} />}

          {error && !isLoading && (
            <div className="border hairline-strong bg-white p-8 text-center">
              <p className="text-destructive mb-4">{t("errors.generic")}</p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-stone-50 hover:bg-brand transition-colors focus-visible-ring"
              >
                {t("common.retry")}
              </button>
            </div>
          )}

          {!isLoading && !error && trips.length === 0 && (
            <div className="border hairline-strong bg-white p-10 md:p-14 text-center">
              <div className="inline-flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-hairline-strong" />
                <span className="text-eyebrow uppercase text-ink-subtle">
                  {t("trips.noTripsEyebrow", "No departures on this date")}
                </span>
                <span className="h-px w-8 bg-hairline-strong" />
              </div>
              <h3 className="font-display text-display-md text-ink max-w-lg mx-auto">
                {t(
                  "trips.noTripsHeadline",
                  "Try an adjacent date — prices are shown above.",
                )}
              </h3>
              <p className="text-sm text-ink-muted mt-4 max-w-md mx-auto leading-relaxed">
                {t("trips.noTripsDescription")}
              </p>
              <button
                onClick={handleBackToSearch}
                className="inline-flex items-center gap-2 mt-7 px-5 py-2.5 border hairline-strong text-ink hover:border-ink transition-colors focus-visible-ring"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("trips.backToSearch")}
              </button>
            </div>
          )}

          {!isLoading && !error && sortedTrips.length > 0 && (
            <div className="space-y-5">
              <ResultsHeader
                tripCount={sortedTrips.length}
                tripType={tripType}
                currentSort={sortBy}
                onSortChange={setSortBy}
              />
              {sortedTrips.map((trip) => (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  passengers={passengers}
                  tripType={tripType}
                  date={date}
                  fromId={fromId}
                  toId={toId}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t hairline-strong bg-white">
        <div className="mx-auto max-w-5xl px-6 py-5 flex items-center justify-between">
          <span className="text-xs text-ink-subtle">
            {t("common.poweredBy")}
          </span>
          <span className="text-xs text-ink-subtle tabular">
            © {new Date().getFullYear()} {tenant?.name}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default TripSearchResultsPage;
